/**
 * TaskCompleteModal — 태스크 완료 처리 + 완료 문서 저장
 *
 * "완료 처리" 시:
 * 1. AI가 전체 타임라인(원본 쓰레드 + 카드 댓글)을 분석
 * 2. 요약 + 카테고리 추천 + 태그 추출
 * 3. 사용자 확인/수정 후 원본 태스크 카드의 metadata.completion에 병합 저장
 * 4. 태스크 status를 'done'으로 변경 (별도 보드/포스트 생성 없음)
 */
import React, { useState, useEffect } from 'react';
import { Trans, t } from '@lingui/macro';
import {
  Sparkles, Loader2, Tag, FolderOpen,
  CheckCircle2, X, FileText,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { useToast } from '../../hooks/use-toast';
import { analyzeForRecord, RecordAnalysisResult } from '../../services/TaskAnalyzer';
import { storeArchiveEmbedding } from '../../services/ArchiveSearchService';
import {
  fetchComments, completeTaskCard, fetchTaskCards,
} from '../../services/BoardService';
import { Post, PostAuthor, getTaskMetadata } from '../../models/Board';
import { My } from '../../services/My';
import { cn } from '../../lib/utils';

interface TaskCompleteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  post: Post;                     // 완료할 태스크 카드
  threadText: string;             // 원본 쓰레드 텍스트
  roomId: string;
  /** 같은 Tasks 보드의 기존 완료 카드 카테고리(추천용) */
  existingCategoryOptions?: string[];
  onCompleted: (updatedPost: Post) => void;
}

const TaskCompleteModal: React.FC<TaskCompleteModalProps> = ({
  open, onOpenChange, post, threadText, existingCategoryOptions, onCompleted,
}) => {
  const { toast } = useToast();
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [, setAnalysis] = useState<RecordAnalysisResult | null>(null);

  // 편집 가능 필드
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [keyDecisions, setKeyDecisions] = useState<string[]>([]);
  const [outcome, setOutcome] = useState('');

  // 기존 카테고리 목록 (추천 시 참고)
  const [existingCategories, setExistingCategories] = useState<string[]>([]);

  // AI 분석 실행 — open이 바뀔 때만 실행
  useEffect(() => {
    if (!open) {
      setAnalysis(null);
      setTitle('');
      setSummary('');
      setCategory('');
      setTags([]);
      setKeyDecisions([]);
      setOutcome('');
      return;
    }

    let cancelled = false;
    setAnalyzing(true);

    const loadedCategories = existingCategoryOptions ?? [];
    setExistingCategories(loadedCategories);

    (async () => {
      try {
        // 카드 댓글 수집
        const comments = await fetchComments(post.id);
        const commentTexts = comments.map(
          c => `${c.author.name}: ${c.text}`
        );

        const result = await analyzeForRecord(
          threadText,
          commentTexts,
          post.title,
          loadedCategories,
          navigator.language || 'ko',
        );

        if (cancelled) return;
        setAnalysis(result);
        setTitle(result.title);
        setSummary(result.summary);
        setCategory(result.suggestedCategory);
        setTags(result.tags);
        setKeyDecisions(result.keyDecisions);
        setOutcome(result.outcome);
      } catch (err) {
        if (cancelled) return;
        console.error('[TaskCompleteModal] AI 분석 실패:', err);
        toast({
          title: t`AI analysis failed. Please fill in details manually.`,
          variant: 'destructive',
        });
        setTitle(post.title);
        setSummary(post.plain_text || '');
      } finally {
        if (!cancelled) setAnalyzing(false);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 완료 처리 + 완료 문서 저장 (원본 태스크 metadata에 병합)
  async function handleSave() {
    if (!title.trim()) {
      toast({ title: t`Please enter a title.`, variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const meta = getTaskMetadata(post);
      // 참여자 수집: 작성자 + 담당자 + 댓글 작성자
      // (워쳐는 '관심 표시'일 뿐 실제 기여자가 아니므로 완료 문서 참여자에서 제외)
      const participantMap = new Map<string, PostAuthor>();
      participantMap.set(post.author.uid, post.author);
      if (meta?.assignee) {
        participantMap.set(meta.assignee.uid, meta.assignee);
      }
      // 댓글 작성자 추가
      const taskComments = await fetchComments(post.id);
      for (const c of taskComments) {
        if (c.author?.uid && !participantMap.has(c.author.uid)) {
          participantMap.set(c.author.uid, c.author);
        }
      }

      const updatedTask = await completeTaskCard(post, {
        title: title.trim(),
        summary: summary.trim(),
        category: category.trim(),
        tags,
        keyDecisions,
        outcome: outcome.trim(),
        participants: Array.from(participantMap.values()).map(p => ({
          uid: p.uid,
          name: p.name,
          photoUrl: p.photoUrl ?? null,
        })),
      }, My.user?.id
        ? { uid: My.user.id, name: My.user.name, photoUrl: My.user.photoUrl || null }
        : undefined);

      // 시맨틱 검색용 임베딩 생성 (fire-and-forget) — 완료된 태스크 post.id를 직접 인덱싱
      const embeddingText = [title, summary, outcome].filter(Boolean).join(' ');
      storeArchiveEmbedding(updatedTask.id, embeddingText).catch(err =>
        console.warn('[TaskCompleteModal] 임베딩 저장 실패 (무시):', err),
      );

      toast({ title: t`Task completed and document saved` });
      onCompleted(updatedTask);
      onOpenChange(false);
    } catch (err) {
      console.error('[TaskCompleteModal] 저장 실패:', err);
      toast({
        title: t`Failed to save completion document`,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  }

  function removeTag(index: number) {
    setTags(prev => prev.filter((_, i) => i !== index));
  }

  function removeDecision(index: number) {
    setKeyDecisions(prev => prev.filter((_, i) => i !== index));
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-full top-auto left-0 right-0 bottom-0 translate-x-0 translate-y-0 rounded-t-2xl rounded-b-none p-4 max-h-[92vh] overflow-y-auto data-[state=open]:slide-in-from-bottom-full data-[state=closed]:slide-out-to-bottom-full data-[state=open]:slide-in-from-top-0 data-[state=closed]:slide-out-to-top-0 sm:max-w-[480px] sm:top-1/2 sm:left-1/2 sm:right-auto sm:bottom-auto sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-2xl sm:p-6 sm:max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-heading-sm font-bold text-foreground">
            <FileText className="size-[18px] text-badge-success" />
            <Trans>Complete Task & Save Document</Trans>
          </DialogTitle>
        </DialogHeader>

        {analyzing ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12">
            <div className="relative">
              <Loader2 className="size-8 text-primary animate-spin" />
              <Sparkles className="size-4 text-amber-500 absolute -top-1 -right-1" />
            </div>
            <p className="text-sm text-muted-foreground">
              <Trans>AI is summarizing the task history...</Trans>
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4 pt-2">
            {/* 제목 */}
            <div>
              <label className="text-meta-sm font-semibold text-foreground mb-1 block">
                <Trans>Document Title</Trans>
              </label>
              <Input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder={t`Title for the completion document`}
                autoFocus
              />
            </div>

            {/* 요약 */}
            <div>
              <label className="text-meta-sm font-semibold text-foreground mb-1 block">
                <Trans>Content</Trans>
              </label>
              <textarea
                value={summary}
                onChange={e => setSummary(e.target.value)}
                placeholder={t`Comprehensive summary of the task`}
                className="w-full min-h-[100px] rounded-md border border-border bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none"
              />
            </div>

            {/* 카테고리 */}
            <div>
              <label className="text-meta-sm font-semibold text-foreground mb-1 flex items-center gap-1">
                <FolderOpen className="size-3" />
                <Trans>Category</Trans>
              </label>
              <Input
                value={category}
                onChange={e => setCategory(e.target.value)}
                placeholder={t`Category for this completion document`}
              />
              {/* 기존 카테고리 추천 칩 */}
              {existingCategories.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1.5">
                  {existingCategories.map(cat => (
                    <button
                      key={cat}
                      type="button"
                      onClick={() => setCategory(cat)}
                      className={cn(
                        'px-2.5 py-1 rounded-full text-meta-sm font-medium transition-colors cursor-pointer border',
                        category === cat
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border text-muted-foreground hover:border-primary/40',
                      )}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* 태그 */}
            <div>
              <label className="text-meta-sm font-semibold text-foreground mb-1 flex items-center gap-1">
                <Tag className="size-3" />
                <Trans>Tags</Trans>
              </label>
              <div className="flex flex-wrap gap-1.5">
                {tags.map((tag, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-0.5 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-meta-sm font-medium"
                  >
                    {tag}
                    <button onClick={() => removeTag(i)} className="ml-0.5 hover:text-destructive">
                      <X className="size-3" />
                    </button>
                  </span>
                ))}
                {tags.length === 0 && (
                  <span className="text-meta-sm text-muted-foreground">
                    <Trans>No tags</Trans>
                  </span>
                )}
              </div>
            </div>

            {/* 핵심 결정사항 */}
            {keyDecisions.length > 0 && (
              <div>
                <label className="text-meta-sm font-semibold text-foreground mb-1 flex items-center gap-1">
                  <CheckCircle2 className="size-3" />
                  <Trans>Key Decisions</Trans>
                </label>
                <ul className="space-y-1">
                  {keyDecisions.map((d, i) => (
                    <li key={i} className="text-meta text-muted-foreground flex items-start gap-1.5 group">
                      <span className="mt-0.5 size-1.5 rounded-full bg-emerald-400/60 shrink-0" />
                      <span className="flex-1">{d}</span>
                      <button
                        onClick={() => removeDecision(i)}
                        className="opacity-0 group-hover:opacity-100 text-destructive/50 hover:text-destructive transition-opacity"
                      >
                        <X className="size-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* 최종 결과 */}
            <div>
              <label className="text-meta-sm font-semibold text-foreground mb-1 flex items-center gap-1">
                <FileText className="size-3" />
                <Trans>Outcome</Trans>
              </label>
              <Input
                value={outcome}
                onChange={e => setOutcome(e.target.value)}
                placeholder={t`Final outcome of this task`}
              />
            </div>

            {/* 버튼 */}
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                <Trans>Cancel</Trans>
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !title.trim()}
                className="gap-1.5"
              >
                {saving ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <FileText className="size-4" />
                )}
                <Trans>Save Completion Document</Trans>
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TaskCompleteModal;
